import { useMemo, useState, type FormEvent, type ReactNode } from "react";
import {
  Activity,
  Building2,
  CreditCard,
  FileCheck2,
  Headphones,
  LogOut,
  Menu,
  Network,
  RadioTower,
  ScrollText,
  RefreshCw,
  ShieldAlert,
  ShieldCheck,
  X,
} from "lucide-react";

import { RaqozLogo } from "@/components/brand/raqoz-logo";
import { ContentEditor } from "@/content-editor";
import { ErrorState, Loading } from "@/components/empty-state";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  decideOrganisationPlanChange,
  platformMissionControlSnapshot,
  platformSupportWorkspace,
  updatePlatformSupportCase,
  type PlatformSupportSeverity,
  type PlatformSupportStatus,
} from "@/lib/api";
import { useData } from "@/lib/app-view";
import { supabase } from "@/lib/supabase";
import { displayDate, titleCase } from "@/lib/utils";
import { TrustedNetworkAdmin } from "@/supplier-network";

type MissionSection =
  | "overview"
  | "tenants"
  | "operations"
  | "commercial"
  | "editorial"
  | "network"
  | "support"
  | "security"
  | "release";

const sections: {
  id: MissionSection;
  label: string;
  icon: typeof Activity;
}[] = [
  { id: "overview", label: "Platform overview", icon: Activity },
  { id: "tenants", label: "Tenant health", icon: Building2 },
  { id: "operations", label: "Service operations", icon: RadioTower },
  { id: "commercial", label: "Commercial queue", icon: CreditCard },
  { id: "editorial", label: "Editorial desk", icon: ScrollText },
  { id: "network", label: "Trusted network", icon: Network },
  { id: "support", label: "Support queue", icon: Headphones },
  { id: "security", label: "Security boundaries", icon: ShieldCheck },
  { id: "release", label: "Release readiness", icon: FileCheck2 },
];

export function MissionControl({
  user,
}: {
  user: { id: string; name: string; email: string };
}) {
  const [section, setSection] = useState<MissionSection>("overview");
  const [refresh, setRefresh] = useState(0);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { data, error, loading } = useData(
    () =>
      Promise.all([
        platformMissionControlSnapshot(),
        platformSupportWorkspace(null),
      ]).then(([snapshot, support]) => ({ snapshot, support })),
    [refresh],
  );

  if (loading) return <Loading />;
  if (error || !data) return <ErrorState message={error} />;
  const snapshot = data.snapshot;
  const support = data.support;

  return (
    <div className="min-h-screen bg-[#071c26] text-white">
      <a href="#mission-content" className="sr-only z-[100] rounded bg-white px-4 py-2 text-slate-950 focus:not-sr-only focus:fixed focus:left-4 focus:top-4">
        Skip to Mission Control content
      </a>
      <header className="sticky top-0 z-40 flex h-16 items-center gap-3 border-b border-white/10 bg-[#071c26] px-4 lg:hidden">
        <Button aria-label="Open Mission Control navigation" variant="ghost" onClick={() => setMobileOpen(true)}>
          <Menu className="size-5" />
        </Button>
        <RaqozLogo inverse />
        <span className="ml-auto text-xs font-bold tracking-[.12em] text-emerald-300">MISSION</span>
      </header>
      {mobileOpen && (
        <button aria-label="Close Mission Control navigation" className="fixed inset-0 z-40 bg-black/60 lg:hidden" onClick={() => setMobileOpen(false)} />
      )}
      <aside
        aria-label="Mission Control navigation"
        className={`fixed inset-y-0 left-0 z-50 flex w-[300px] flex-col border-r border-white/10 bg-[#071c26] p-5 transition-transform lg:translate-x-0 ${mobileOpen ? "translate-x-0" : "-translate-x-full"}`}
      >
        <div className="flex items-center justify-between">
          <RaqozLogo inverse />
          <Button aria-label="Close Mission Control navigation" className="lg:hidden" variant="ghost" onClick={() => setMobileOpen(false)}>
            <X className="size-5" />
          </Button>
        </div>
        <p className="mt-8 text-[11px] font-black tracking-[.16em] text-emerald-300">CYCLOTRON OPERATIONS</p>
        <p className="mt-2 text-sm leading-6 text-white/55">Aggregate platform evidence. No tenant impersonation.</p>
        <nav className="mt-7 grid gap-1">
          {sections.map((item) => {
            const Icon = item.icon;
            return (
              <button
                className={`flex min-h-11 items-center gap-3 rounded-xl px-3 text-left text-sm font-semibold transition ${section === item.id ? "bg-emerald-300 text-[#071c26]" : "text-white/65 hover:bg-white/10 hover:text-white"}`}
                key={item.id}
                onClick={() => {
                  setSection(item.id);
                  setMobileOpen(false);
                }}
              >
                <Icon className="size-4" aria-hidden="true" />
                {item.label}
              </button>
            );
          })}
        </nav>
        <div className="mt-auto border-t border-white/10 pt-5">
          <p className="truncate text-sm font-semibold">{user.name}</p>
          <p className="truncate text-xs text-white/45">{user.email}</p>
          <Button className="mt-3 w-full justify-start text-white hover:bg-white/10" variant="ghost" onClick={() => void supabase.auth.signOut()}>
            <LogOut className="size-4" /> Sign out
          </Button>
        </div>
      </aside>
      <main id="mission-content" className="min-h-screen p-5 lg:ml-[300px] lg:p-8 xl:p-10">
        <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="text-xs font-black tracking-[.16em] text-emerald-300">RAQOZ MISSION CONTROL</p>
            <h1 className="mt-2 font-serif text-4xl tracking-[-.035em]">{sections.find((item) => item.id === section)?.label}</h1>
            <p className="mt-2 text-sm text-white/55">Snapshot generated {displayDate(snapshot.generated_at)}</p>
          </div>
          <Button className="bg-white text-[#071c26] hover:bg-white/90" onClick={() => setRefresh((value) => value + 1)}>
            <RefreshCw className="size-4" /> Refresh evidence
          </Button>
        </div>

        {section === "overview" && <MissionOverview snapshot={snapshot} />}
        {section === "tenants" && <TenantHealth tenants={snapshot.tenants || []} />}
        {section === "operations" && <Operations snapshot={snapshot} />}
        {section === "commercial" && <CommercialQueue requests={snapshot.plan_changes || []} changed={() => setRefresh((value) => value + 1)} />}
        {section === "editorial" && <LightWorkspace><ContentEditor platformOwner /></LightWorkspace>}
        {section === "network" && <LightWorkspace><TrustedNetworkAdmin /></LightWorkspace>}
        {section === "support" && <SupportQueue userId={user.id} support={support} changed={() => setRefresh((value) => value + 1)} />}
        {section === "security" && <SecurityBoundary snapshot={snapshot} />}
        {section === "release" && <ReleaseReadiness snapshot={snapshot} />}
      </main>
    </div>
  );
}

function MissionOverview({ snapshot }: { snapshot: any }) {
  const attention = (snapshot.tenants || []).filter((tenant: any) => tenant.health !== "green");
  return (
    <>
      <section aria-label="Platform summary" className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-6">
        <Metric label="Tenants" value={snapshot.summary?.tenants || 0} detail={`${snapshot.summary?.active_tenants || 0} active`} />
        <Metric label="Members" value={snapshot.summary?.active_members || 0} detail="Active identities" />
        <Metric label="Attention" value={attention.length} detail="Amber or red tenants" />
        <Metric label="Support" value={snapshot.summary?.open_support || 0} detail="Open cases" />
        <Metric label="Delivery" value={snapshot.summary?.failed_integrations || 0} detail="Failed events" />
        <Metric label="Plan queue" value={snapshot.summary?.pending_plan_changes || 0} detail="Awaiting decision" />
      </section>
      <section className="mt-6 grid gap-5 xl:grid-cols-[1.25fr_.75fr]">
        <DarkPanel>
          <div className="flex items-center justify-between gap-4">
            <div><p className="eyebrow text-emerald-300">ATTENTION QUEUE</p><h2 className="mt-2 text-2xl font-semibold">Tenant health exceptions</h2></div>
            <Badge value={attention.length ? "attention" : "healthy"} />
          </div>
          <div className="mt-5 grid gap-3">
            {attention.length === 0 && <p className="rounded-xl bg-white/5 p-4 text-sm text-white/60">Every tenant is currently green.</p>}
            {attention.slice(0, 8).map((tenant: any) => (
              <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-white/10 p-4" key={tenant.id}>
                <div><strong>{tenant.name}</strong><p className="mt-1 text-xs text-white/50">{tenant.plan?.name || "No plan"} · {tenant.members?.active || 0} active members</p></div>
                <Health value={tenant.health} />
              </div>
            ))}
          </div>
        </DarkPanel>
        <DarkPanel>
          <p className="eyebrow text-emerald-300">OPERATING BOUNDARY</p>
          <h2 className="mt-2 text-2xl font-semibold">Evidence without entry</h2>
          <ul className="mt-5 grid gap-3 text-sm text-white/65">
            <Boundary ok label="Aggregate tenant health only" />
            <Boundary ok label="No procurement document links" />
            <Boundary ok label="No user impersonation" />
            <Boundary ok label="Case-based customer support" />
            <Boundary ok label="Immutable operational reasons" />
          </ul>
        </DarkPanel>
      </section>
    </>
  );
}

function TenantHealth({ tenants }: { tenants: any[] }) {
  const [query, setQuery] = useState("");
  const filtered = tenants.filter((tenant) =>
    [tenant.name, tenant.slug, tenant.plan?.name, tenant.health]
      .join(" ")
      .toLowerCase()
      .includes(query.trim().toLowerCase()),
  );
  return (
    <>
      <Label className="max-w-md text-white/70">
        Find tenant
        <input className="min-h-11 rounded-xl border border-white/15 bg-white/5 px-3 text-white placeholder:text-white/35" placeholder="Name, slug, plan or health" value={query} onChange={(event) => setQuery(event.target.value)} />
      </Label>
      <div className="mt-6 grid gap-4 xl:grid-cols-2">
        {filtered.map((tenant) => (
          <DarkPanel key={tenant.id}>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div><h2 className="text-xl font-semibold">{tenant.name}</h2><p className="mt-1 text-xs text-white/45">{tenant.slug} · created {displayDate(tenant.created_at)}</p></div>
              <Health value={tenant.health} />
            </div>
            <dl className="mt-5 grid gap-3 text-sm sm:grid-cols-2">
              <DarkFact label="Plan" value={tenant.plan?.name || "No subscription"} />
              <DarkFact label="Subscription" value={titleCase(tenant.plan?.status || "not configured")} />
              <DarkFact label="Active members" value={String(tenant.members?.active || 0)} />
              <DarkFact label="Administrators" value={String(tenant.members?.administrators || 0)} />
              <DarkFact label="Failed deliveries" value={String(tenant.operations?.failed_integrations || 0)} />
              <DarkFact label="Open support" value={String(tenant.operations?.open_support || 0)} />
              <DarkFact label="Activity (24h)" value={String(tenant.operations?.audit_events_24h || 0)} />
              <DarkFact label="Last activity" value={tenant.operations?.last_activity_at ? displayDate(tenant.operations.last_activity_at) : "No activity"} />
            </dl>
          </DarkPanel>
        ))}
      </div>
    </>
  );
}

function Operations({ snapshot }: { snapshot: any }) {
  const groups = [
    ["Integration outbox", snapshot.operations?.outbox || {}],
    ["Subscriptions", snapshot.operations?.subscriptions || {}],
    ["Support queue", snapshot.operations?.support || {}],
    ["CMS workflow", snapshot.operations?.content || {}],
  ] as const;
  return (
    <div className="grid gap-5 xl:grid-cols-2">
      {groups.map(([name, values]) => (
        <DarkPanel key={name}>
          <p className="eyebrow text-emerald-300">PLATFORM REGISTER</p>
          <h2 className="mt-2 text-2xl font-semibold">{name}</h2>
          <dl className="mt-5 grid gap-3 sm:grid-cols-2">
            {Object.entries(values).map(([label, value]) => <DarkFact key={label} label={titleCase(label)} value={String(value)} />)}
            {Object.keys(values).length === 0 && <p className="text-sm text-white/50">No records in this register.</p>}
          </dl>
        </DarkPanel>
      ))}
    </div>
  );
}

function CommercialQueue({
  requests,
  changed,
}: {
  requests: any[];
  changed: () => void;
}) {
  const [selectedId, setSelectedId] = useState("");
  const [decision, setDecision] = useState<"approved" | "rejected">("approved");
  const [reason, setReason] = useState("");
  const [externalReference, setExternalReference] = useState("");
  const [periodEnd, setPeriodEnd] = useState("");
  const [formError, setFormError] = useState("");
  const [busy, setBusy] = useState(false);
  const selected = requests.find((item) => item.id === selectedId);
  const paidApproval =
    decision === "approved" &&
    selected?.requested_plan?.code !== "launch";

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!selected) return;
    setBusy(true);
    setFormError("");
    try {
      await decideOrganisationPlanChange(
        selected.organisation.id,
        selected.id,
        {
          decision,
          reason,
          externalReference: paidApproval ? externalReference : undefined,
          currentPeriodEndsAt:
            paidApproval && periodEnd
              ? new Date(`${periodEnd}T23:59:59.000Z`).toISOString()
              : undefined,
        },
      );
      setSelectedId("");
      setReason("");
      setExternalReference("");
      setPeriodEnd("");
      changed();
    } catch (cause) {
      setFormError((cause as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="grid gap-5 xl:grid-cols-[1.1fr_.9fr]">
      <div className="grid content-start gap-4">
        {requests.length === 0 && (
          <DarkPanel>
            <h2 className="text-xl font-semibold">No pending plan changes</h2>
            <p className="mt-2 text-sm text-white/50">
              Tenant requests will appear here for governed commercial decision.
            </p>
          </DarkPanel>
        )}
        {requests.map((item) => (
          <button
            className={`rounded-2xl border p-5 text-left transition ${
              selectedId === item.id
                ? "border-emerald-300 bg-emerald-300/10"
                : "border-white/10 bg-white/[.04] hover:bg-white/[.07]"
            }`}
            key={item.id}
            onClick={() => setSelectedId(item.id)}
          >
            <p className="text-xs font-bold tracking-[.12em] text-white/45">
              {item.organisation?.name}
            </p>
            <h2 className="mt-2 text-lg font-semibold">
              {item.current_plan?.name} → {item.requested_plan?.name}
            </h2>
            <p className="mt-3 text-sm leading-6 text-white/55">
              {item.request_reason}
            </p>
            <p className="mt-3 text-xs text-white/35">
              Requested by {item.requester?.name} · {displayDate(item.requested_at)}
            </p>
          </button>
        ))}
      </div>
      <DarkPanel>
        {!selected ? (
          <>
            <h2 className="text-xl font-semibold">Select a plan request</h2>
            <p className="mt-2 text-sm text-white/50">
              Approval records the exact plan, period and external commercial evidence.
              RAQOZ does not collect a manual-contract payment here.
            </p>
          </>
        ) : (
          <form className="grid gap-4" onSubmit={submit}>
            <div>
              <p className="eyebrow text-emerald-300">COMMERCIAL DECISION</p>
              <h2 className="mt-2 text-xl font-semibold">
                {selected.organisation?.name}
              </h2>
              <p className="mt-2 text-sm text-white/50">
                {selected.current_plan?.name} → {selected.requested_plan?.name}
              </p>
            </div>
            <Label className="text-white/70">
              Decision
              <select
                className="min-h-11 rounded-xl border border-white/15 bg-[#0d2b36] px-3 text-white"
                value={decision}
                onChange={(event) =>
                  setDecision(event.target.value as "approved" | "rejected")
                }
              >
                <option value="approved">Approve</option>
                <option value="rejected">Reject</option>
              </select>
            </Label>
            <Label className="text-white/70">
              Decision reason
              <Textarea
                className="border-white/15 bg-white/5 text-white"
                minLength={10}
                value={reason}
                onChange={(event) => setReason(event.target.value)}
                required
              />
            </Label>
            {paidApproval && (
              <>
                <Label className="text-white/70">
                  External commercial reference
                  <Input
                    className="border-white/15 bg-white/5 text-white"
                    value={externalReference}
                    onChange={(event) => setExternalReference(event.target.value)}
                    required
                  />
                </Label>
                <Label className="text-white/70">
                  Paid period ends
                  <Input
                    className="border-white/15 bg-white/5 text-white"
                    type="date"
                    value={periodEnd}
                    onChange={(event) => setPeriodEnd(event.target.value)}
                    required
                  />
                </Label>
              </>
            )}
            {formError && (
              <p role="alert" className="rounded-xl bg-red-950/60 p-3 text-sm text-red-100">
                {formError}
              </p>
            )}
            <Button
              className="bg-emerald-300 text-[#071c26] hover:bg-emerald-200"
              disabled={busy}
              type="submit"
            >
              {busy ? "Recording…" : "Record commercial decision"}
            </Button>
          </form>
        )}
      </DarkPanel>
    </div>
  );
}

function SupportQueue({
  userId,
  support,
  changed,
}: {
  userId: string;
  support: { cases: any[]; events: any[] };
  changed: () => void;
}) {
  const [selectedId, setSelectedId] = useState("");
  const [status, setStatus] = useState<PlatformSupportStatus>("in_progress");
  const [severity, setSeverity] = useState<PlatformSupportSeverity>("normal");
  const [reason, setReason] = useState("");
  const [resolution, setResolution] = useState("");
  const [formError, setFormError] = useState("");
  const [busy, setBusy] = useState(false);
  const selected = support.cases.find((item) => item.id === selectedId);
  const events = useMemo(() => support.events.filter((event) => event.case_id === selectedId), [support.events, selectedId]);

  async function update(event: FormEvent) {
    event.preventDefault();
    if (!selected) return;
    setBusy(true);
    setFormError("");
    try {
      await updatePlatformSupportCase(selected.id, {
        status,
        severity,
        assignedTo: userId,
        reason,
        resolution: ["resolved", "closed"].includes(status) ? resolution : null,
      });
      setSelectedId("");
      setReason("");
      setResolution("");
      changed();
    } catch (cause) {
      setFormError((cause as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="grid gap-5 xl:grid-cols-[1.1fr_.9fr]">
      <div className="grid content-start gap-4">
        {support.cases.map((item) => (
          <button
            className={`rounded-2xl border p-5 text-left transition ${selectedId === item.id ? "border-emerald-300 bg-emerald-300/10" : "border-white/10 bg-white/[.04] hover:bg-white/[.07]"}`}
            key={item.id}
            onClick={() => {
              setSelectedId(item.id);
              setStatus(item.status === "open" ? "in_progress" : item.status);
              setSeverity(item.severity);
            }}
          >
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div><p className="text-xs font-bold tracking-[.12em] text-white/45">{item.organisation?.name} · {titleCase(item.category)}</p><h2 className="mt-1 text-lg font-semibold">{item.subject}</h2></div>
              <div className="flex gap-2"><Badge value={item.severity} /><Badge value={item.status} /></div>
            </div>
            <p className="mt-3 line-clamp-2 text-sm leading-6 text-white/55">{item.description}</p>
          </button>
        ))}
      </div>
      <DarkPanel>
        {!selected ? (
          <><h2 className="text-xl font-semibold">Select a support case</h2><p className="mt-2 text-sm text-white/50">Operational actions require a reason and append immutable evidence.</p></>
        ) : (
          <>
            <p className="eyebrow text-emerald-300">CASE CONTROL</p>
            <h2 className="mt-2 text-xl font-semibold">{selected.subject}</h2>
            <p className="mt-3 text-sm leading-6 text-white/60">{selected.description}</p>
            <form className="mt-5 grid gap-4" onSubmit={update}>
              <Label className="text-white/70">Status<select className="min-h-11 rounded-xl border border-white/15 bg-[#0d2b36] px-3 text-white" value={status} onChange={(event) => setStatus(event.target.value as PlatformSupportStatus)}>{["triaged","in_progress","waiting_customer","resolved","closed","open"].map((item) => <option value={item} key={item}>{titleCase(item)}</option>)}</select></Label>
              <Label className="text-white/70">Severity<select className="min-h-11 rounded-xl border border-white/15 bg-[#0d2b36] px-3 text-white" value={severity} onChange={(event) => setSeverity(event.target.value as PlatformSupportSeverity)}>{["low","normal","high","critical"].map((item) => <option value={item} key={item}>{titleCase(item)}</option>)}</select></Label>
              <Label className="text-white/70">Operational reason<Textarea className="border-white/15 bg-white/5 text-white" minLength={12} value={reason} onChange={(event) => setReason(event.target.value)} required /></Label>
              {["resolved","closed"].includes(status) && <Label className="text-white/70">Resolution<Textarea className="border-white/15 bg-white/5 text-white" minLength={12} value={resolution} onChange={(event) => setResolution(event.target.value)} required /></Label>}
              {formError && <p role="alert" className="rounded-xl bg-red-950/60 p-3 text-sm text-red-100">{formError}</p>}
              <Button disabled={busy} className="bg-emerald-300 text-[#071c26] hover:bg-emerald-200" type="submit">{busy ? "Recording…" : "Record governed action"}</Button>
            </form>
            <div className="mt-6 border-t border-white/10 pt-5">
              <h3 className="font-semibold">Evidence history</h3>
              <div className="mt-3 grid gap-2">
                {events.map((event) => <div className="rounded-xl bg-white/5 p-3 text-sm" key={event.id}><strong>{titleCase(event.event_type)}</strong><p className="mt-1 text-white/55">{event.reason}</p><p className="mt-1 text-xs text-white/35">{event.actor?.name || "System"} · {displayDate(event.created_at)}</p></div>)}
              </div>
            </div>
          </>
        )}
      </DarkPanel>
    </div>
  );
}

function SecurityBoundary({ snapshot }: { snapshot: any }) {
  return (
    <div className="grid gap-5 xl:grid-cols-[1fr_1fr]">
      <DarkPanel>
        <ShieldCheck className="size-8 text-emerald-300" />
        <h2 className="mt-4 text-2xl font-semibold">Platform access boundary</h2>
        <dl className="mt-5 grid gap-3">
          <DarkFact label="Platform administrators" value={String(snapshot.security?.active_platform_administrators || 0)} />
          <DarkFact label="Tenant document access" value="Not exposed" />
          <DarkFact label="Impersonation" value="Disabled" />
          <DarkFact label="Support access" value="Case based" />
        </dl>
      </DarkPanel>
      <DarkPanel>
        <ShieldAlert className="size-8 text-amber-300" />
        <h2 className="mt-4 text-2xl font-semibold">Launch guardrails</h2>
        <ul className="mt-5 grid gap-3 text-sm text-white/65">
          <Boundary ok label="No customer password controls" />
          <Boundary ok label="No cross-tenant procurement navigation" />
          <Boundary ok label="No hidden role switching" />
          <Boundary ok label="No break-glass impersonation" />
          <Boundary ok label="All support transitions require reasons" />
          <Boundary ok label="Support events cannot be edited or deleted" />
        </ul>
      </DarkPanel>
    </div>
  );
}

function ReleaseReadiness({ snapshot }: { snapshot: any }) {
  const unhealthy = (snapshot.tenants || []).filter((tenant: any) => tenant.health === "red").length;
  const failed = Number(snapshot.summary?.failed_integrations || 0);
  const checks = [
    ["No red tenant health", unhealthy === 0, `${unhealthy} red tenants`],
    ["Integration delivery clear", failed === 0, `${failed} failed or dead-letter events`],
    ["Platform administrator present", Number(snapshot.security?.active_platform_administrators || 0) > 0, `${snapshot.security?.active_platform_administrators || 0} active`],
    ["Impersonation disabled", snapshot.security?.impersonation === "disabled", String(snapshot.security?.impersonation || "unknown")],
    ["Support workflow governed", snapshot.security?.support_access === "case_based_without_impersonation", "Case-based without impersonation"],
  ];
  const ready = checks.every((item) => item[1]);
  const build = import.meta.env.VITE_APP_COMMIT_SHA || "Set VITE_APP_COMMIT_SHA during publication";
  return (
    <div className="grid gap-5 xl:grid-cols-[1.15fr_.85fr]">
      <DarkPanel>
        <div className="flex items-center justify-between gap-4"><div><p className="eyebrow text-emerald-300">LIVE REGISTER</p><h2 className="mt-2 text-2xl font-semibold">Operational launch gate</h2></div><Health value={ready ? "green" : "amber"} /></div>
        <div className="mt-5 grid gap-3">
          {checks.map(([label, passed, detail]) => <div className="flex items-start gap-3 rounded-xl border border-white/10 p-4" key={String(label)}><ShieldCheck className={`mt-0.5 size-5 shrink-0 ${passed ? "text-emerald-300" : "text-amber-300"}`} /><div><strong>{label}</strong><p className="mt-1 text-sm text-white/45">{detail}</p></div></div>)}
        </div>
      </DarkPanel>
      <DarkPanel>
        <p className="eyebrow text-emerald-300">PUBLICATION EVIDENCE</p>
        <h2 className="mt-2 text-2xl font-semibold">Exact build</h2>
        <code className="mt-4 block break-all rounded-xl bg-black/25 p-4 text-xs text-emerald-200">{build}</code>
        <p className="mt-4 text-sm leading-6 text-white/55">Publication remains a separate controlled action after application, database, migration, lint and pgTAP gates pass.</p>
      </DarkPanel>
    </div>
  );
}

function LightWorkspace({ children }: { children: ReactNode }) {
  return (
    <section className="rounded-2xl bg-[#f5f7f6] p-5 text-[#102733] sm:p-7">
      {children}
    </section>
  );
}

function DarkPanel({ children }: { children: ReactNode }) {
  return <section className="rounded-2xl border border-white/10 bg-white/[.04] p-5 shadow-[0_24px_70px_rgba(0,0,0,.18)] sm:p-6">{children}</section>;
}

function Metric({ label, value, detail }: { label: string; value: string | number; detail: string }) {
  return <DarkPanel><p className="text-xs font-bold tracking-[.12em] text-white/40">{label.toUpperCase()}</p><p className="mt-2 text-3xl font-semibold">{value}</p><p className="mt-1 text-sm text-white/45">{detail}</p></DarkPanel>;
}

function DarkFact({ label, value }: { label: string; value: string }) {
  return <div className="rounded-xl bg-white/5 p-3"><dt className="text-xs text-white/40">{label}</dt><dd className="mt-1 font-semibold text-white">{value}</dd></div>;
}

function Health({ value }: { value: string }) {
  const colours: Record<string, string> = { green: "bg-emerald-300 text-emerald-950", amber: "bg-amber-300 text-amber-950", red: "bg-red-300 text-red-950" };
  return <span className={`inline-flex min-h-7 items-center rounded-full px-3 text-xs font-black uppercase tracking-[.1em] ${colours[value] || "bg-slate-300 text-slate-950"}`}>{value === "green" ? "Healthy" : value === "amber" ? "Attention" : value === "red" ? "Blocked" : titleCase(value)}</span>;
}

function Boundary({ ok, label }: { ok: boolean; label: string }) {
  return <li className="flex items-center gap-3"><ShieldCheck className={`size-4 shrink-0 ${ok ? "text-emerald-300" : "text-red-300"}`} />{label}</li>;
}
